import { CheckCircleIcon, ClockIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { GraphQLError } from 'graphql'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { LineItem as BuyersLineItem } from '@/buyers/_gen/gql'
import type { LineItem } from '@/types'

import { UNKNOWN_ERROR } from '../providers/MsgsProvider'

import useAddAltMpn from '../hooks/useAddAltMpn'
import useConfig from '../hooks/useConfig'
import useMsgs from '../hooks/useMsgs'
import useToggle from '../hooks/useToggle'

import Money from '../modules/Money'
import ProductM from '../modules/Product'
import TimeM from '../modules/Time'
import ModalForm from './ModalForm'

import A from './A'
import Action from './Action'
import Card from './Card'
import Pill from './Pill'
import TextField from './TextField'
import Tooltip from './Tooltip'

export const InStockPill = ({ className }: { className?: string }) => (
  <Pill className={classNames('bg-green-100 text-green-700', className)}>
    <CheckCircleIcon className="-mx-1 w-5 h-5 min-w-5 min-h-5" />
    <span>In Stock</span>
  </Pill>
)
export const LeadTimePill = ({
  availableAt,
  className,
}: {
  availableAt: DateTime
  className?: string
}) => (
  <Pill className={classNames('bg-yellow-100 text-yellow-700', className)}>
    <ClockIcon className="-mx-1 w-5 h-5 min-w-5 min-h-5" />
    <span>{TimeM.formatDateStringNoYear(availableAt)}</span>
  </Pill>
)

type PropsLineItem = Pick<
  BuyersLineItem,
  | 'id'
  | 'unitPrice'
  | 'discountedUnitPrice'
  | 'quantity'
  | 'extendedPrice'
  | 'availableAt'
  | 'inStock'
> & {
  product: Pick<
    LineItem['product'],
    'id' | 'name' | 'images' | 'enabled' | 'offline' | 'path' | 'mpn' | 'altMpn'
  >
  rfqPart: Pick<Exclude<BuyersLineItem['rfqPart'], null>, 'mpn'> | null
}

const LineItem = ({
  item,
  isAdmin = false,
  showMpn = true,
  linkToMarketplace = false,
}: {
  item: PropsLineItem
  isAdmin?: boolean
  showMpn?: boolean
  linkToMarketplace?: boolean
}) => {
  const { gfBaseUrl } = useConfig()
  const [_msgs, msgsMgr] = useMsgs()
  const [altMpn, setAltMpn] = useState<string>('')
  const [addAltMpnModalOpen, addAltMpnModalToggler] = useToggle()
  const [activeLineItem, setActiveLineItem] = useState<PropsLineItem>()
  const addAltMpn = useAddAltMpn()

  const prepAndOpenAltMpnModal = () => {
    setActiveLineItem(item)
    addAltMpnModalToggler.on()
  }

  return (
    <>
      <Card.SubSection>
        <div className="flex gap-4">
          {ProductM.primaryImageURL(item.product, 'card') ===
            '/svg/gf/product-image-placeholder.svg' && (
            <img
              src={ProductM.primaryImageURL(item.product, 'card')}
              alt={item.product.name}
              className="w-16 h-16 border rounded-sm overflow-hidden object-cover"
            />
          )}

          <div className="flex flex-1 flex-col text-sm gap-1">
            <div className="flex flex-row justify-between gap-x-4 flex-wrap">
              {linkToMarketplace ? (
                ProductM.marketplaceUrl(item.product, gfBaseUrl) ? (
                  <A.T href={ProductM.marketplaceUrl(item.product, gfBaseUrl)}>
                    {item.product.name}
                  </A.T>
                ) : (
                  <span>{item.product.name}</span>
                )
              ) : (
                <Link to={`/products/${item.product.id}`} className="text-blue-500 underline">
                  {item.product.name}
                </Link>
              )}

              <div className="flex flex-row space-x-2 sm:space-x-4">
                <p className="text-sm text-gray-700">
                  <span className="space-x-2">
                    {!Money.equals(item.unitPrice, item.discountedUnitPrice) && (
                      <span className="line-through text-gray-500">
                        {Money.format(item.unitPrice)}
                      </span>
                    )}

                    <span>{Money.format(item.discountedUnitPrice)}</span>
                  </span>

                  <span> x {item.quantity}</span>
                </p>

                <p className="text-sm text-gray-700">{Money.format(item.extendedPrice)}</p>
              </div>
            </div>

            {showMpn && (
              <div className="flex gap-2 items-center">
                <div className="text-gray-500">MPN: {item.product.mpn}</div>

                {item.rfqPart && item.rfqPart.mpn && item.rfqPart.mpn !== item.product.mpn && (
                  <div className="px-2 py-1 bg-gray-100 border border-gray-300 rounded-md text-gray-600">
                    Requested MPN: <span className="font-medium">{item.rfqPart.mpn}</span>
                  </div>
                )}
              </div>
            )}

            {isAdmin && (
              <div className="flex space-x-2 text-sm">
                {item.product.altMpn && (
                  <p className="text-gray-500">Alt MPN: {item.product.altMpn}</p>
                )}

                <Action.T onClick={() => prepAndOpenAltMpnModal()}>
                  Edit {item.product.altMpn ? '' : 'Alt MPN'}
                </Action.T>
              </div>
            )}

            {item.inStock ? (
              <div className="pt-1">
                <InStockPill className="self-start" />
              </div>
            ) : (
              item.inStock !== null &&
              item.availableAt && (
                <div className="pt-1 flex flex-row justify-start items-center space-x-1 sm:space-x-1.5">
                  <LeadTimePill availableAt={item.availableAt} />

                  <Tooltip
                    text="This is the estimated date that the Vendor indicated the part will be ready at"
                    size="medium"
                  />
                </div>
              )
            )}
          </div>
        </div>
      </Card.SubSection>

      <ModalForm
        open={addAltMpnModalOpen}
        onClose={addAltMpnModalToggler.off}
        title="Edit Alt Mpn"
        onSubmit={(e) => {
          e.preventDefault()

          if (activeLineItem) {
            addAltMpn(activeLineItem.product.id, altMpn)
              .then((_result) => {
                msgsMgr.add('Alt MPN Updated! Refresh the page', 'positive')
              })
              .catch((err: GraphQLError) => {
                msgsMgr.add(err.message || UNKNOWN_ERROR, 'negative')
              })
              .finally(addAltMpnModalToggler.off)
          }
        }}
        submitButtonText="Update"
      >
        <TextField
          label="Alt Mpn"
          required
          defaultValue={activeLineItem?.product.altMpn || ''}
          onChange={(e) => setAltMpn(e.target.value)}
        />
      </ModalForm>
    </>
  )
}

export default LineItem
